import React from "react";
import { HelpCircle } from "lucide-react";
import { Checkbox, Label, Tooltip } from "@/components/ui";
import { highlightHtmlSnippet } from "./highlightHtmlSnippet";
import { CopyButton } from "./PreviewPanel";

interface Props {
  snippet: string;
  includeAutoResize: boolean;
  onIncludeAutoResizeChange: (includeAutoResize: boolean) => void;
}

const EmbedSnippet: React.FC<Props> = ({ snippet, includeAutoResize, onIncludeAutoResizeChange }) => (
  <div className="min-w-0 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-200 bg-slate-100 px-3 py-2">
      <Label className="text-slate-600">Embed code</Label>

      <div className="flex items-center gap-1.5">
        <Checkbox
          id="include-auto-resize"
          label="Auto-resize"
          checked={includeAutoResize}
          onCheckedChange={checked => onIncludeAutoResizeChange(!!checked)}
        />
        <Tooltip>
          <Tooltip.Trigger
            render={
              <button
                type="button"
                className="inline-flex items-center text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                aria-label="About auto-resize"
              >
                <HelpCircle className="size-3.5" />
              </button>
            }
          />
          <Tooltip.Content className="max-w-64">
            When enabled, the snippet listens for height updates from the embed and resizes the iframe to fit. Turn it
            off to keep a fixed height with scrollable content.
          </Tooltip.Content>
        </Tooltip>
      </div>

      <div className="ml-auto flex items-center gap-1">
        <CopyButton value={snippet} label="Copy code" copiedMessage="Embed code copied" variant="ghost" size="sm" />
      </div>
    </div>
    <pre className="m-0 overflow-x-auto whitespace-pre p-4 font-mono text-[14px] leading-6 selection:bg-slate-300/60">
      {highlightHtmlSnippet(snippet)}
    </pre>
  </div>
);

const autoResizeSnippet = (src: string, height: number, iframeId: string, heightMessageType: string): string =>
  `<iframe id="${iframeId}" src="${src}" width="100%" height="${height}"></iframe>
<!-- Auto-resizes the iframe to match content height -->
<script>
  window.addEventListener("message", function (e) {
    if (e.data && e.data.type === "${heightMessageType}" && typeof e.data.height === "number") {
      var f = document.getElementById("${iframeId}");
      if (f) f.style.height = e.data.height + "px";
    }
  });
</script>`;

export { EmbedSnippet, autoResizeSnippet };
